// Sprint 2026-Feb-11i — Estimate Email Panel + Manual Send + Preview Dialog
//
// Rendered inside the expanded row of the Estimate list. Shows:
//   • POC recipient email (resolved from Client POC master).
//   • CC list (from Email Config).
//   • Initial send status + timestamp + tracking aggregates
//     (first/last sent, total sent/failed, last failure reason,
//     last attachment name, pending-days badge).
//   • Reminder toggle + last / next reminder time + count.
//   • "Send Email" button → opens EstimateManualSendDialog.
//   • "Preview Email" button (standalone) → opens PreviewDialog for
//     dry-run verification of the auto-send / current template.
//   • "Email History" toggle → shows the full chronological email_logs
//     table with failure reason + attachment columns.
//
// The panel is READ-ONLY when the estimate is not yet saved (id absent).

import { useEffect, useMemo, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Mail, Send, BellOff, Bell, ChevronRight, ChevronDown, History,
  Eye, Code, FileText, Download, ArrowLeft, AlertTriangle,
} from "lucide-react";

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(dt); }
}

export default function EstimateEmailPanel({ estimateId, statusHint }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [openSend, setOpenSend] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);

  useEffect(() => {
    if (!estimateId) return;
    reload();
  }, [estimateId]);

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get(`/estimates/${estimateId}/email-info`);
      setInfo(data);
    } catch (e) {
      // Silent — if user lacks estimate_email.view we just hide the panel.
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    try {
      const { data } = await api.get(`/estimates/${estimateId}/email-history`);
      setHistory(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    }
  }

  async function toggleReminder(next) {
    try {
      await api.post(`/estimates/${estimateId}/reminder-toggle`, { enabled: !!next });
      toast.success(next ? "Reminder enabled" : "Reminder disabled");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  }

  if (loading) return (
    <div className="text-xs text-slate-400 italic" data-testid="est-email-panel-loading">Loading email status…</div>
  );
  if (!info) return null;   // permission-gated

  const statusColor = ({
    sent:    "text-emerald-700 bg-emerald-50 border-emerald-200",
    failed:  "text-rose-700 bg-rose-50 border-rose-200",
    skipped: "text-slate-600 bg-slate-100 border-slate-200",
    queued:  "text-amber-700 bg-amber-50 border-amber-200",
  })[info.last_email_status] || "text-slate-500 bg-slate-50 border-slate-200";

  return (
    <div className="border border-slate-200 bg-white p-3 mt-3 space-y-2"
         data-testid={`est-email-panel-${estimateId}`}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-slate-500" />
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Email Status</div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline"
                  onClick={() => setOpenPreview(true)}
                  data-testid="est-preview-email-btn">
            <Eye size={12} className="mr-1" /> Preview Email
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => setOpenSend(true)}
                  data-testid="est-send-email-btn"
                  disabled={!info.email_enabled_globally}>
            <Send size={12} className="mr-1" /> Send Email
          </Button>
          {info.reminder_enabled ? (
            <Button size="sm" variant="outline"
                    onClick={() => toggleReminder(false)}
                    data-testid="est-reminder-off"
                    disabled={statusHint === "Approved"}>
              <BellOff size={12} className="mr-1" /> Stop Reminder
            </Button>
          ) : (
            <Button size="sm" variant="outline"
                    onClick={() => toggleReminder(true)}
                    data-testid="est-reminder-on"
                    disabled={statusHint === "Approved"}>
              <Bell size={12} className="mr-1" /> Enable Reminder
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-slate-700">
        <Row label="Recipient (POC)"
             value={info.recipient
               ? <span className="font-mono text-slate-800">{info.recipient}</span>
               : <span className="text-rose-600 italic">POC has no email on file</span>} />
        <Row label="Sender POC Name" value={info.poc_name || "—"} />
        <Row label="CC" value={(info.cc || []).join(", ") || "—"} />

        <Row label="First Sent"   value={fmt(info.first_email_at)} testid="stat-first-sent" />
        <Row label="Initial Sent" value={fmt(info.initial_email_at)} />
        <Row label="Last Sent"    value={fmt(info.last_email_at)} />

        <Row label="Last Status"
             value={<span className={`inline-block px-1.5 py-0.5 border text-[10px] font-bold uppercase tracking-wider ${statusColor}`}
                          data-testid="stat-last-status">
                       {info.last_email_status || "—"}
                     </span>} />
        <Row label="Total Sent"
             value={<span className="font-bold text-emerald-700" data-testid="stat-total-sent">
               {info.total_sent || 0}
             </span>} />
        <Row label="Total Failed"
             value={<span className={`font-bold ${(info.total_failed || 0) > 0 ? "text-rose-700" : "text-slate-500"}`}
                          data-testid="stat-total-failed">
               {info.total_failed || 0}
             </span>} />
        <Row label="Total Skipped"
             value={<span className={`font-bold ${(info.total_skipped || 0) > 0 ? "text-amber-700" : "text-slate-500"}`}
                          data-testid="stat-total-skipped">
               {info.total_skipped || 0}
             </span>} />
        <Row label="Auto-Send (Initial)"
             value={<span className={`inline-block px-1.5 py-0.5 border text-[10px] font-bold uppercase tracking-wider ${
                 info.auto_send_enabled
                   ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                   : "text-rose-700 bg-rose-50 border-rose-200"
               }`} data-testid="stat-auto-send">
               {info.auto_send_enabled ? "ON" : "OFF"}
             </span>} />

        <Row label="Reminder Enabled" value={info.reminder_enabled ? "Yes" : "No"} />
        <Row label="Reminders Sent" value={`${info.reminder_count || 0} / ${info.max_reminders || 7}`} />
        <Row label="Next Reminder" value={fmt(info.next_reminder_at)} />

        {/* Enhancement 2026-Feb-27 — pending days badge, mirrors {{pending_days}} placeholder */}
        <Row label="Pending Since"
             value={
               <span className={`inline-block px-1.5 py-0.5 border text-[10px] font-bold ${
                 (info.pending_days || 0) >= 3
                   ? "text-amber-800 bg-amber-50 border-amber-200"
                   : "text-slate-700 bg-slate-50 border-slate-200"
               }`}>
                 {info.pending_days || 0} day{(info.pending_days || 0) === 1 ? "" : "s"}
               </span>} />
        <Row label="Last Attachment"
             value={info.last_attachment_name
               ? <span className="font-mono text-[10px] text-slate-700 truncate" title={info.last_attachment_name}
                       data-testid="stat-last-attachment">{info.last_attachment_name}</span>
               : <span className="text-slate-400 italic">—</span>} />
      </div>

      {/* Prominent failure reason panel — only visible when there IS a recent failure. */}
      {info.last_failure_reason ? (
        <div className="border border-rose-200 bg-rose-50 p-2 flex items-start gap-2"
             data-testid="stat-last-failure-reason">
          <AlertTriangle size={14} className="text-rose-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">
              Last Failure {info.last_failure_type ? `(${info.last_failure_type})` : ""}
              {info.last_failure_at ? ` · ${fmt(info.last_failure_at)}` : ""}
            </div>
            <div className="text-[11px] text-rose-800 break-all mt-0.5">{info.last_failure_reason}</div>
          </div>
        </div>
      ) : null}

      {/* Jul-2026 — last SKIP reason (e.g. auto-send off / POC without email).
          Skipped initials previously looked like "nothing happened". */}
      {info.last_skip_reason ? (
        <div className="border border-amber-200 bg-amber-50 p-2 flex items-start gap-2"
             data-testid="stat-last-skip-reason">
          <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
              Last Skipped {info.last_skip_type ? `(${info.last_skip_type})` : ""}
              {info.last_skip_at ? ` · ${fmt(info.last_skip_at)}` : ""}
            </div>
            <div className="text-[11px] text-amber-800 break-all mt-0.5">{info.last_skip_reason}</div>
          </div>
        </div>
      ) : null}

      <button className="text-[11px] text-orange-700 hover:underline inline-flex items-center gap-1"
              onClick={() => { setShowHistory(v => !v); if (!showHistory) loadHistory(); }}
              data-testid="est-email-history-toggle">
        {showHistory ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <History size={12} /> Email History ({info.total_emails || 0})
      </button>
      {showHistory && <HistoryTable rows={history} />}

      {openSend && (
        <ManualSendDialog
          estimateId={estimateId}
          initial={info}
          onClose={() => setOpenSend(false)}
          onSent={() => { setOpenSend(false); reload(); if (showHistory) loadHistory(); }}
        />
      )}

      {openPreview && (
        <PreviewDialog
          estimateId={estimateId}
          initialInfo={info}
          initialOverride={null}     // standalone preview → no override
          onClose={() => setOpenPreview(false)}
          onConfirmSend={null}       // standalone preview → no "confirm & send"
        />
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline gap-2" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 min-w-[120px]">{label}</span>
      <span className="text-[11px] text-slate-800 break-all">{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Full-width chronological email history table with expand-row
// showing full subject + failure reason + attachment.
// ─────────────────────────────────────────────────────────────────
function HistoryTable({ rows }) {
  const [expanded, setExpanded] = useState(null);
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-slate-200 p-3" data-testid="est-email-history-table">
        <div className="text-xs text-slate-400 italic">No email history yet.</div>
      </div>
    );
  }
  return (
    <div className="border border-slate-200 max-h-96 overflow-y-auto" data-testid="est-email-history-table">
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
          <tr>
            <th className="w-6"></th>
            {["When", "Type", "To", "Subject", "Status", "Attachment", "By"].map(h =>
              <th key={h} className="text-left py-1.5 px-2 text-[9px] uppercase font-bold text-slate-500">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <Fragment key={r.id}>
              <tr className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                  data-testid={`history-row-${r.id}`}>
                <td className="px-1">
                  <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                          className="text-slate-400 hover:text-slate-700"
                          data-testid={`history-expand-${r.id}`}>
                    {expanded === r.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                </td>
                <td className="py-1.5 px-2 whitespace-nowrap">{fmt(r.sent_at)}</td>
                <td className="py-1.5 px-2 uppercase text-[10px] font-bold text-slate-600">{r.type}</td>
                <td className="py-1.5 px-2 font-mono">{r.recipient || "—"}</td>
                <td className="py-1.5 px-2 max-w-xs truncate" title={r.subject || ""}>{r.subject || "—"}</td>
                <td className="py-1.5 px-2">
                  <span className={`inline-block px-1 py-0.5 border text-[9px] font-bold uppercase ${
                    r.status === "sent"
                      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                      : r.status === "failed"
                        ? "text-rose-700 bg-rose-50 border-rose-200"
                        : "text-slate-600 bg-slate-100 border-slate-200"
                  }`}>{r.status}</span>
                </td>
                <td className="py-1.5 px-2 font-mono text-[10px] truncate max-w-[180px]"
                    title={r.attachment_name || ""}>
                  {r.attachment_name || "—"}
                </td>
                <td className="py-1.5 px-2 text-[10px] text-slate-500">{r.triggered_by || "—"}</td>
              </tr>
              {expanded === r.id && (
                <tr className="bg-slate-50 border-b border-slate-100">
                  <td colSpan={8} className="p-3 space-y-1.5" data-testid={`history-detail-${r.id}`}>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Subject</div>
                    <div className="text-[11px] text-slate-800">{r.subject || "—"}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-2">CC</div>
                    <div className="text-[11px] font-mono text-slate-700">{(r.cc || []).join(", ") || "—"}</div>
                    {r.failure_reason ? (
                      <>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 mt-2">Failure Reason</div>
                        <div className="text-[11px] text-rose-800 break-all bg-rose-50 border border-rose-200 p-1.5">
                          {r.failure_reason}
                        </div>
                      </>
                    ) : null}
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-2">Attachment</div>
                    <div className="text-[11px] font-mono text-slate-700">{r.attachment_name || "—"}</div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Manual Send Dialog — spec §8. Adds a "Preview" button that hands
// the current draft to <PreviewDialog/> in dry-run mode; the preview
// dialog can then either send it back for edits or confirm-and-send.
// ─────────────────────────────────────────────────────────────────
function ManualSendDialog({ estimateId, initial, onClose, onSent }) {
  const [to, setTo]         = useState(initial.recipient || "");
  const [cc, setCc]         = useState((initial.cc || []).join(", "));
  const [subject, setSub]   = useState("");
  const [body, setBody]     = useState("");
  const [attachPdf, setAtt] = useState(initial.attach_pdf !== false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const currentOverride = useMemo(() => {
    const cc_list = cc.split(/[,\n]/g).map(s => s.trim()).filter(Boolean);
    const p = { to, cc: cc_list, attach_pdf: attachPdf };
    if (subject.trim()) p.subject = subject;
    if (body.trim())    p.body = body;
    return p;
  }, [to, cc, subject, body, attachPdf]);

  async function send() {
    if (!to || !to.includes("@")) { toast.error("Enter a valid recipient"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/estimates/${estimateId}/send-email`, currentOverride);
      if (data.status === "sent") toast.success(`Email sent to ${data.recipient}`);
      else if (data.status === "failed") toast.error(`Send failed: ${data.failure_reason || "unknown"}`);
      else toast.warning(`Skipped: ${data.failure_reason || "?"}`);
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  }

  const dialog = createPortal(
    <>
      <div className="fixed inset-0 z-[100] bg-black/40" onClick={onClose} data-testid="manual-send-backdrop" />
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white shadow-xl w-full max-w-2xl pointer-events-auto"
             data-testid={`manual-send-dialog-${estimateId}`}>
          <div className="px-5 py-3 border-b flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-800 text-sm">Send Estimate Email</h3>
              <div className="text-[10px] text-slate-500 font-mono">{initial.voucher_number}</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
          </div>
          <div className="p-5 space-y-3">
            <Field label="To"><Input value={to} onChange={e => setTo(e.target.value)} data-testid="manual-to" /></Field>
            <Field label="CC (comma separated)"><Input value={cc} onChange={e => setCc(e.target.value)} data-testid="manual-cc" /></Field>
            <Field label="Subject (leave blank to use template)">
              <Input value={subject} onChange={e => setSub(e.target.value)}
                     placeholder="Leave blank to use template" data-testid="manual-subject" />
            </Field>
            <Field label="Body (HTML — leave blank to use template)">
              <Textarea rows={8} value={body} onChange={e => setBody(e.target.value)}
                        placeholder="Leave blank to use template" data-testid="manual-body" />
            </Field>
            <div className="flex items-center gap-3 border rounded px-3 py-2 bg-slate-50">
              <Label className="text-xs font-semibold flex-1">Attach Estimate PDF</Label>
              <Switch checked={attachPdf} onCheckedChange={setAtt} data-testid="manual-attach-pdf" />
            </div>
          </div>
          <div className="px-5 py-3 border-t flex justify-between items-center gap-2">
            <Button variant="outline" onClick={() => setShowPreview(true)}
                    disabled={sending} data-testid="manual-preview">
              <Eye size={12} className="mr-1" /> Preview
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={sending} data-testid="manual-cancel">Cancel</Button>
              <Button onClick={send} disabled={sending} data-testid="manual-send"
                      className="bg-orange-600 hover:bg-orange-700 text-white">
                <Send size={12} className="mr-1" /> {sending ? "Sending…" : "Send"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <>
      {dialog}
      {showPreview && (
        <PreviewDialog
          estimateId={estimateId}
          initialInfo={initial}
          initialOverride={currentOverride}
          onClose={() => setShowPreview(false)}
          onConfirmSend={async () => {
            setShowPreview(false);
            await send();
          }}
        />
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Preview Dialog — Sprint 2026-Feb-11i
//
// True read-only dry-run render. Backend `POST /preview-email` never
// touches the estimate, email_logs, reminder counters, or scheduler.
//
// Displays:
//   • Envelope (From / Reply-To / To / CC / Subject)
//   • Context (Estimate #, Client, POC, Amount, Project, Pending Days)
//   • Fully-rendered sanitized HTML body (default view)
//   • Optional "View HTML Source" toggle (admin only) — read-only text
//   • Attachment name + "View PDF" (opens existing voucher-pdf endpoint)
//
// When invoked with a `onConfirmSend` callback (i.e. from the Manual
// Send dialog), the footer shows both "Back to Edit" and
// "Confirm & Send" buttons.
// ═════════════════════════════════════════════════════════════════════
function PreviewDialog({ estimateId, initialInfo, initialOverride, onClose, onConfirmSend }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showSource, setShowSource] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setErr(null);
        const payload = initialOverride ? { ...initialOverride, type: "manual" }
                                        : { type: "manual" };
        const { data: d } = await api.post(`/estimates/${estimateId}/preview-email`, payload);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Preview failed to load");
      }
    }
    load();
    return () => { cancelled = true; };
  }, [estimateId, initialOverride]);

  async function viewPdf() {
    setPdfLoading(true);
    try {
      const res = await api.get(`/estimates/${estimateId}/voucher-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      // Revoke after a delay so the tab has time to fetch.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to open PDF");
    } finally { setPdfLoading(false); }
  }

  const safeHtml = useMemo(() => {
    if (!data?.body_html) return "";
    return DOMPurify.sanitize(data.body_html, { USE_PROFILES: { html: true } });
  }, [data?.body_html]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[200] bg-black/50" onClick={onClose} data-testid="preview-backdrop" />
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col pointer-events-auto"
             data-testid={`preview-dialog-${estimateId}`}>
          <div className="px-5 py-3 border-b flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-800 text-sm inline-flex items-center gap-2">
                <Eye size={14} className="text-slate-500" /> Email Preview
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                  Read-only Dry-run
                </span>
              </h3>
              <div className="text-[10px] text-slate-500 font-mono">{initialInfo?.voucher_number || estimateId}</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none"
                    data-testid="preview-close">×</button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {err && (
              <div className="border border-rose-200 bg-rose-50 p-3 text-[12px] text-rose-800"
                   data-testid="preview-error">
                {err}
              </div>
            )}
            {!data && !err && (
              <div className="text-xs text-slate-400 italic" data-testid="preview-loading">
                Rendering preview…
              </div>
            )}
            {data && (
              <>
                {/* Envelope */}
                <div className="border border-slate-200">
                  <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Envelope
                  </div>
                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                    <Meta label="From" value={data.from_name ? `${data.from_name} <${data.from_email}>` : data.from_email} testid="preview-from" />
                    <Meta label="Reply-To" value={data.reply_to || "—"} testid="preview-reply-to" />
                    <Meta label="To" value={data.to || <span className="text-rose-600 italic">— none —</span>} testid="preview-to" />
                    <Meta label="CC" value={(data.cc || []).join(", ") || "—"} testid="preview-cc" />
                    <Meta label="Subject" value={data.subject || "—"} full testid="preview-subject" />
                  </div>
                </div>

                {/* Context values (placeholders resolved) */}
                <div className="border border-slate-200">
                  <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Context Values (Placeholders Resolved)
                  </div>
                  <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-[11px]">
                    <Meta label="Estimate #" value={data.context?.estimate_number || "—"} testid="ctx-estimate-number" />
                    <Meta label="Client Name" value={data.context?.client_name || "—"} testid="ctx-client-name" />
                    <Meta label="Client POC" value={data.context?.client_poc_name || "—"} testid="ctx-poc-name" />
                    <Meta label="Amount" value={data.context?.estimate_amount || "—"} testid="ctx-estimate-amount" />
                    <Meta label="Project" value={data.context?.project_name || "—"} testid="ctx-project-name" />
                    <Meta label="Pending Days"
                          value={<span className={(data.context?.pending_days || 0) >= 3
                                    ? "text-amber-800 font-bold" : ""}>
                                   {data.context?.pending_days ?? 0}
                                 </span>}
                          testid="ctx-pending-days" />
                    <Meta label="Estimate Date" value={data.context?.estimate_date || "—"} testid="ctx-estimate-date" />
                    <Meta label="Branch" value={data.context?.branch_name || "—"} testid="ctx-branch-name" />
                    <Meta label="Company" value={data.context?.company_name || "—"} testid="ctx-company-name" />
                  </div>
                </div>

                {/* Rendered body */}
                <div className="border border-slate-200">
                  <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                      Rendered Email Body
                    </div>
                    {isAdmin && (
                      <button onClick={() => setShowSource(v => !v)}
                              className="text-[10px] uppercase tracking-wider font-bold text-orange-700 hover:underline inline-flex items-center gap-1"
                              data-testid="preview-toggle-source">
                        <Code size={11} />
                        {showSource ? "Hide HTML Source" : "View HTML Source"}
                      </button>
                    )}
                  </div>
                  <div className="p-3">
                    {showSource ? (
                      <pre className="text-[11px] font-mono bg-slate-900 text-emerald-300 p-3 overflow-x-auto max-h-96"
                           data-testid="preview-html-source">{data.body_html}</pre>
                    ) : (
                      <div className="border border-slate-100 bg-white p-4 min-h-[120px] max-h-96 overflow-y-auto text-[13px] text-slate-800 leading-relaxed"
                           data-testid="preview-rendered-body"
                           dangerouslySetInnerHTML={{ __html: safeHtml }} />
                    )}
                  </div>
                </div>

                {/* Attachment */}
                <div className="border border-slate-200">
                  <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Attachment
                  </div>
                  <div className="p-3 flex items-center justify-between gap-3 flex-wrap">
                    {data.attach_pdf && data.attachment_name ? (
                      <>
                        <div className="flex items-center gap-2 text-[12px] text-slate-800 min-w-0">
                          <FileText size={14} className="text-slate-500 flex-shrink-0" />
                          <span className="font-mono truncate" title={data.attachment_name}
                                data-testid="preview-attachment-name">{data.attachment_name}</span>
                        </div>
                        <Button size="sm" variant="outline" onClick={viewPdf} disabled={pdfLoading}
                                data-testid="preview-view-pdf">
                          <Download size={12} className="mr-1" />
                          {pdfLoading ? "Loading…" : "View PDF"}
                        </Button>
                      </>
                    ) : (
                      <div className="text-[12px] text-slate-500 italic" data-testid="preview-no-attachment">
                        No PDF attachment for this send.
                      </div>
                    )}
                  </div>
                </div>

                <div className="border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800"
                     data-testid="preview-noop-notice">
                  This is a <b>read-only dry-run</b>. No email is sent, no log entry is created, and no
                  estimate data (status / reminder count / last-sent) is modified.
                </div>
              </>
            )}
          </div>

          <div className="px-5 py-3 border-t flex justify-end gap-2">
            {onConfirmSend ? (
              <>
                <Button variant="outline" onClick={onClose} data-testid="preview-back-to-edit">
                  <ArrowLeft size={12} className="mr-1" /> Back to Edit
                </Button>
                <Button onClick={onConfirmSend} disabled={!data || !!err}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                        data-testid="preview-confirm-send">
                  <Send size={12} className="mr-1" /> Confirm &amp; Send
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={onClose} data-testid="preview-close-btn">Close</Button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

function Meta({ label, value, full, testid }) {
  return (
    <div className={`flex items-baseline gap-2 ${full ? "md:col-span-2" : ""}`} data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 min-w-[90px]">{label}</span>
      <span className="text-[11px] text-slate-800 break-all flex-1">{value}</span>
    </div>
  );
}
